import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Edit, LogOut, Settings, FileText, Trash2, Eye, Users, RefreshCw, Paperclip, Download, X, Search, Filter, FileDown, Calendar as CalendarIcon, Lock, Unlock, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function Dashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attachmentDialog, setAttachmentDialog] = useState({ open: false, job: null });
  const [draftDialog, setDraftDialog] = useState({ open: false, job: null });
  const [draftPagesMode, setDraftPagesMode] = useState('all');
  const [draftPagesSpec, setDraftPagesSpec] = useState('');
  const [draftLoading, setDraftLoading] = useState(false);

  // Reconciliation
  const [reconDialog, setReconDialog] = useState({ open: false, job: null });
  const [reconForm, setReconForm] = useState({ zoho_invoice_no: '', invoice_raised: false, payment_received: false });
  const [reconLoading, setReconLoading] = useState(false);
  const [salespersons, setSalespersons] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSalesperson, setFilterSalesperson] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const BASE_URL = API.replace('/api', '');

  const parseAttachment = (a) => {
    if (a == null) return null;
    if (typeof a === 'object') return a;
    if (typeof a === 'string') {
      // Try JSON first (newer schema)
      try {
        const obj = JSON.parse(a);
        if (obj && typeof obj === 'object') return obj;
      } catch {
        // not JSON — treat as raw URL/path
      }
      // Bare string — could be a path like "/api/files/foo.png" or just a filename
      const url = a.startsWith('/') || a.startsWith('http') ? a : `/api/files/${a}`;
      const name = a.split('/').pop();
      return { original_name: name, stored_name: name, url, content_type: '' };
    }
    return null;
  };

  const isImageAttachment = (att) => {
    const parsed = parseAttachment(att);
    return parsed.content_type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(parsed.original_name || parsed.url || '');
  };

  const getAttachments = (job) => {
    if (!job.attachments || job.attachments.length === 0) return [];
    return job.attachments.map(parseAttachment).filter(Boolean);
  };

  useEffect(() => {
    const loadJobs = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('token');
        const [jobsRes, salespersonsRes] = await Promise.all([
          axios.get(`${API}/jobs`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }),
          axios.get(`${API}/salespersons`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }).catch(() => ({ data: [] }))
        ]);
        
        if (Array.isArray(jobsRes.data)) {
          setJobs(jobsRes.data);
        } else {
          setJobs([]);
        }
        setSalespersons(salespersonsRes.data || []);
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
        toast.error('Failed to load jobs: ' + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    };
    
    loadJobs();
  }, []);

  const filteredJobs = jobs.filter(job => {
    if (searchText) {
      const q = searchText.toLowerCase();
      const matches = (job.job_card || '').toLowerCase().includes(q) ||
                      (job.customer_name || '').toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (filterDateFrom && job.date < filterDateFrom) return false;
    if (filterDateTo && job.date > filterDateTo) return false;
    if (filterSalesperson !== 'all' && job.salesperson_id !== filterSalesperson) return false;
    if (filterStatus !== 'all' && job.status !== filterStatus) return false;
    return true;
  });

  const clearFilters = () => {
    setSearchText('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterSalesperson('all');
    setFilterStatus('all');
  };

  const hasActiveFilters = searchText || filterDateFrom || filterDateTo || filterSalesperson !== 'all' || filterStatus !== 'all';

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/jobs/${jobId}`);
      toast.success('Job deleted successfully');
      // Refresh jobs list
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/jobs`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setJobs(response.data || []);
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const refreshJobs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/jobs`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setJobs(response.data || []);
      toast.success('Jobs refreshed');
    } catch (err) {
      toast.error('Failed to refresh jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPDF = async (jobId, jobCard) => {
    const token = localStorage.getItem('token');
    const pdfUrl = `${API}/jobs/${jobId}/pdf`;
    
    try {
      const response = await fetch(pdfUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText || ''}`.trim());
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to load PDF: ' + (error.message || 'Unknown error'));
    }
  };

  const openDraftDialog = (job) => {
    setDraftDialog({ open: true, job });
    setDraftPagesMode('all');
    setDraftPagesSpec('');
  };

  const handleDownloadDraft = async () => {
    if (!draftDialog.job) return;
    const job = draftDialog.job;
    const pagesParam = draftPagesMode === 'all' ? 'all' : draftPagesSpec.trim();
    if (draftPagesMode === 'specific' && !pagesParam) {
      toast.error('Please enter the page numbers (e.g., 1,3,5-7)');
      return;
    }
    setDraftLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(
        `${API}/jobs/${job.id}/draft-pdf?pages=${encodeURIComponent(pagesParam)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText || ''}`.trim());
      }
      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Server returned an empty file');
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `draft_job_card_${job.job_card}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Draft Job Card downloaded');
      setDraftDialog({ open: false, job: null });
    } catch (error) {
      toast.error('Failed to download draft: ' + (error.message || 'Unknown error'));
    } finally {
      setDraftLoading(false);
    }
  };

  const openReconDialog = (job) => {
    setReconForm({
      zoho_invoice_no: job.zoho_invoice_no || '',
      invoice_raised: !!job.invoice_raised,
      payment_received: !!job.payment_received,
    });
    setReconDialog({ open: true, job });
  };

  const submitReconcile = async (closeFlag) => {
    if (!reconDialog.job) return;
    if (closeFlag) {
      if (!reconForm.zoho_invoice_no.trim() || !reconForm.invoice_raised || !reconForm.payment_received) {
        toast.error('To close: enter Zoho invoice no, tick Invoice Raised and Payment Received.');
        return;
      }
    }
    setReconLoading(true);
    try {
      await axios.post(`${API}/jobs/${reconDialog.job.id}/reconcile`, {
        zoho_invoice_no: reconForm.zoho_invoice_no.trim(),
        invoice_raised: reconForm.invoice_raised,
        payment_received: reconForm.payment_received,
        close: !!closeFlag,
      });
      toast.success(closeFlag ? 'Job closed & locked' : 'Reconciliation saved');
      setReconDialog({ open: false, job: null });
      await refreshJobs();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Unknown error';
      toast.error('Failed: ' + (typeof msg === 'string' ? msg : JSON.stringify(msg)));
    } finally {
      setReconLoading(false);
    }
  };

  const reopenJob = async (jobId) => {
    if (!window.confirm('Reopen this closed job? It will become editable again.')) return;
    try {
      await axios.post(`${API}/jobs/${jobId}/reopen`, {});
      toast.success('Job reopened');
      await refreshJobs();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Unknown error';
      toast.error('Failed to reopen: ' + (typeof msg === 'string' ? msg : JSON.stringify(msg)));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-slate-100 text-slate-600';
      case 'submitted': return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'approved': return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'closed': return 'bg-red-50 text-red-700 border border-red-200';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">Job Cost Tracker</h1>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">{user?.name} ({user?.role})</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/reports')}
              data-testid="reports-nav-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Reports
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/calendar')}
              data-testid="calendar-nav-btn"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendar
            </Button>
            {user?.role === 'admin' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/users')}
                data-testid="users-nav-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Users
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/admin')}
              data-testid="admin-nav-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Admin
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-6 md:p-8">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-1">Jobs</h2>
            <p className="text-slate-600">Manage and track all moving jobs ({jobs.length} total{hasActiveFilters ? `, ${filteredJobs.length} shown` : ''})</p>
          </div>
          <div className="flex gap-2 ml-auto">
            <Button 
              variant="outline"
              onClick={refreshJobs}
              disabled={loading}
              data-testid="refresh-jobs-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              className="bg-slate-900 hover:bg-slate-800" 
              onClick={() => navigate('/jobs/new')}
              data-testid="create-job-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Job
            </Button>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="space-y-3 mt-4 mb-2">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by job card or customer..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={hasActiveFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : ''}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-1" />
              Filters {hasActiveFilters ? '(active)' : ''}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-white border border-slate-200 rounded-lg" data-testid="filter-panel">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Date From</Label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  data-testid="filter-date-from"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Date To</Label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  data-testid="filter-date-to"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Salesperson</Label>
                <Select value={filterSalesperson} onValueChange={setFilterSalesperson}>
                  <SelectTrigger data-testid="filter-salesperson">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Salespersons</SelectItem>
                    {salespersons.map(sp => (
                      <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-0">
            {(() => {
              if (loading) {
                return <div className="p-8 text-center text-slate-600">Loading jobs...</div>;
              }
              if (error) {
                return (
                  <div className="p-8 text-center">
                    <p className="text-red-600 mb-4">Error: {error}</p>
                    <Button onClick={refreshJobs} variant="outline">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again
                    </Button>
                  </div>
                );
              }
              if (filteredJobs.length === 0 && hasActiveFilters) {
                return (
                  <div className="p-8 text-center text-slate-600" data-testid="no-filter-results">
                    No jobs match your filters. <button className="text-blue-600 underline" onClick={clearFilters}>Clear filters</button>
                  </div>
                );
              }
              if (jobs.length === 0) {
                return (
                  <div className="p-8 text-center text-slate-600">
                    No jobs yet. Create your first job to get started.
                  </div>
                );
              }
              return (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Job Card</TableHead>
                      <TableHead className="font-semibold">Customer</TableHead>
                      <TableHead className="font-semibold">Invoice Amount</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Salesperson</TableHead>
                      <TableHead className="font-semibold">Total Cost</TableHead>
                      <TableHead className="font-semibold">Margin</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Zoho #</TableHead>
                      <TableHead className="font-semibold">Files</TableHead>
                      <TableHead className="font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredJobs.map((job) => (
                      <TableRow key={job.id} data-testid={`job-row-${job.id}`}>
                        <TableCell className="font-medium">{job.job_card}</TableCell>
                        <TableCell>{job.customer_name}</TableCell>
                        <TableCell>AED {job.invoice_amount.toFixed(2)}</TableCell>
                        <TableCell>{job.date}</TableCell>
                        <TableCell>{job.salesperson_name || 'N/A'}</TableCell>
                        <TableCell>AED {job.total_cost.toFixed(2)}</TableCell>
                        <TableCell>
                          <span className={job.margin >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                            AED {job.margin.toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(job.status)}>
                            {job.status === 'closed' && <Lock className="w-3 h-3 mr-1 inline" />}
                            {job.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {job.zoho_invoice_no ? (
                            <span className="text-xs font-medium text-slate-700" data-testid={`zoho-${job.id}`}>{job.zoho_invoice_no}</span>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {getAttachments(job).length > 0 ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setAttachmentDialog({ open: true, job })}
                              data-testid={`attachments-${job.id}-btn`}
                              title="View Attachments"
                              className="flex items-center gap-1"
                            >
                              <Paperclip className="w-4 h-4 text-slate-600" />
                              <span className="text-xs font-medium">{getAttachments(job).length}</span>
                            </Button>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleViewPDF(job.id, job.job_card)}
                              data-testid={`view-job-${job.id}-btn`}
                              title="View/Print PDF"
                            >
                              <Eye className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDraftDialog(job)}
                              data-testid={`draft-job-${job.id}-btn`}
                              title="Download Draft Job Card"
                            >
                              <FileDown className="w-4 h-4 text-emerald-600" />
                            </Button>
                            {job.status !== 'closed' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => navigate(`/jobs/${job.id}/edit`)}
                                data-testid={`edit-job-${job.id}-btn`}
                                title="Edit Job"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {user?.role === 'admin' && job.status !== 'closed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openReconDialog(job)}
                                data-testid={`reconcile-job-${job.id}-btn`}
                                title="Reconcile (Zoho / Payment / Close)"
                              >
                                <CheckCircle2 className="w-4 h-4 text-violet-600" />
                              </Button>
                            )}
                            {user?.role === 'admin' && job.status === 'closed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => reopenJob(job.id)}
                                data-testid={`reopen-job-${job.id}-btn`}
                                title="Reopen Closed Job"
                              >
                                <Unlock className="w-4 h-4 text-amber-600" />
                              </Button>
                            )}
                            {user?.role === 'admin' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDelete(job.id)}
                                data-testid={`delete-job-${job.id}-btn`}
                                title="Delete Job"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Attachment Viewer Dialog */}
        <Dialog open={attachmentDialog.open} onOpenChange={(open) => setAttachmentDialog({ open, job: open ? attachmentDialog.job : null })}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle data-testid="attachment-dialog-title">
                Attachments - Job {attachmentDialog.job?.job_card}
              </DialogTitle>
            </DialogHeader>
            {attachmentDialog.job && (
              <div className="space-y-4 mt-2">
                {getAttachments(attachmentDialog.job).length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No attachments</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {getAttachments(attachmentDialog.job).map((att, idx) => (
                      <div key={att.stored_name || att.url || idx} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`attachment-item-${idx}`}>
                        {isImageAttachment(att) ? (
                          <a href={`${BASE_URL}${att.url}`} target="_blank" rel="noopener noreferrer">
                            <img
                              src={`${BASE_URL}${att.url}`}
                              alt={att.original_name}
                              className="w-full h-40 object-cover hover:opacity-90 transition-opacity cursor-pointer"
                            />
                          </a>
                        ) : (
                          <div className="h-40 flex items-center justify-center bg-slate-50">
                            <FileText className="w-12 h-12 text-slate-300" />
                          </div>
                        )}
                        <div className="p-2 flex items-center justify-between gap-2 bg-white">
                          <span className="text-xs text-slate-700 truncate flex-1" title={att.original_name}>
                            {att.original_name || att.stored_name || 'File'}
                          </span>
                          <a
                            href={`${BASE_URL}${att.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0"
                            data-testid={`download-attachment-${idx}`}
                          >
                            <Button variant="ghost" size="sm">
                              <Download className="w-4 h-4 text-blue-600" />
                            </Button>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Draft Job Card Download Dialog */}
        <Dialog open={draftDialog.open} onOpenChange={(open) => setDraftDialog({ open, job: open ? draftDialog.job : null })}>
          <DialogContent className="max-w-md" data-testid="draft-dialog">
            <DialogHeader>
              <DialogTitle>Download Draft Job Card</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-slate-600">
                Job {draftDialog.job?.job_card} — pick which pages to include.
              </p>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="draft-pages-all">
                  <input
                    type="radio"
                    name="draft-pages"
                    value="all"
                    checked={draftPagesMode === 'all'}
                    onChange={() => setDraftPagesMode('all')}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm">All pages</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="draft-pages-specific">
                  <input
                    type="radio"
                    name="draft-pages"
                    value="specific"
                    checked={draftPagesMode === 'specific'}
                    onChange={() => setDraftPagesMode('specific')}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm">Specific pages</span>
                </label>
                {draftPagesMode === 'specific' && (
                  <Input
                    placeholder="e.g. 1,3,5-7"
                    value={draftPagesSpec}
                    onChange={(e) => setDraftPagesSpec(e.target.value)}
                    data-testid="draft-pages-input"
                    className="ml-6"
                  />
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setDraftDialog({ open: false, job: null })}
                  data-testid="draft-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDownloadDraft}
                  disabled={draftLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="draft-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {draftLoading ? 'Generating...' : 'Download'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reconcile Dialog (admin) */}
        <Dialog open={reconDialog.open} onOpenChange={(open) => setReconDialog({ open, job: open ? reconDialog.job : null })}>
          <DialogContent className="max-w-md" data-testid="reconcile-dialog">
            <DialogHeader>
              <DialogTitle>Reconcile Job — {reconDialog.job?.job_card}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-xs text-slate-600">
                Customer: <span className="font-medium">{reconDialog.job?.customer_name}</span> · Invoice AED {Number(reconDialog.job?.invoice_amount || 0).toFixed(2)}
              </p>
              <div className="space-y-2">
                <Label htmlFor="zoho-invoice-no">Zoho Invoice No <span className="text-red-500">*</span></Label>
                <Input
                  id="zoho-invoice-no"
                  value={reconForm.zoho_invoice_no}
                  onChange={(e) => setReconForm({ ...reconForm, zoho_invoice_no: e.target.value })}
                  placeholder="e.g. INV-001234"
                  data-testid="recon-zoho-input"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reconForm.invoice_raised}
                  onChange={(e) => setReconForm({ ...reconForm, invoice_raised: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="recon-invoice-raised"
                />
                <span className="text-sm">Invoice Raised in Zoho</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reconForm.payment_received}
                  onChange={(e) => setReconForm({ ...reconForm, payment_received: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="recon-payment-received"
                />
                <span className="text-sm">Payment Received</span>
              </label>
              <div className="border-t border-slate-200 pt-3 flex justify-between gap-2">
                <Button
                  variant="outline"
                  onClick={() => setReconDialog({ open: false, job: null })}
                  data-testid="recon-cancel-btn"
                >
                  Cancel
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => submitReconcile(false)}
                    disabled={reconLoading}
                    data-testid="recon-save-btn"
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => submitReconcile(true)}
                    disabled={reconLoading || !reconForm.zoho_invoice_no.trim() || !reconForm.invoice_raised || !reconForm.payment_received}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="recon-close-btn"
                  >
                    <Lock className="w-4 h-4 mr-1" />
                    Close Job
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}